import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { fetchBookChapters, LANGUAGE_NAMES } from '../../lib/crossref.js';
import { createEntries, findExistingDois, EMPTY_ENTRY } from '../../lib/bibliography.js';
import AnnounceCheckbox from '../common/AnnounceCheckbox.jsx';

// Language dropdown options, de-duplicated by display name and sorted. Names are
// the English forms the dataset uses for language_display.
const LANGUAGE_OPTIONS = (() => {
  const seen = new Set();
  return Object.entries(LANGUAGE_NAMES)
    .filter(([, name]) => (seen.has(name) ? false : (seen.add(name), true)))
    .map(([code, name]) => ({ code, name }))
    .sort((a, b) => a.name.localeCompare(b.name));
})();

const ERROR_KEYS = {
  NO_IDENTIFIER: 'bibliography.import.errors.noIdentifier',
  NO_ISBN: 'bibliography.import.errors.noIsbn',
  NO_CHAPTERS: 'bibliography.import.errors.noChapters',
  FETCH_FAILED: 'bibliography.import.errors.fetchFailed',
};

const splitPeople = (s) =>
  (s ?? '')
    .split(/\s*;\s*|\n/)
    .map((x) => x.trim())
    .filter(Boolean);

// Paste a book link / DOI / ISBN → load every chapter from Crossref and submit
// the chosen ones as a batch of (pending) bibliography entries.
//
// Props:
//   asAdmin:  publish directly vs. submit for review.
//   authorId: the submitting user's id.
export default function BookImporter({ asAdmin, authorId }) {
  const { t } = useTranslation();
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null); // { book, rows }
  const [shared, setShared] = useState(null);
  const [includeBook, setIncludeBook] = useState(true);
  const [announceBook, setAnnounceBook] = useState(false);
  const [done, setDone] = useState(null); // { created, skipped }

  async function handleFetch(event) {
    event.preventDefault();
    setError(null);
    setDone(null);
    setLoading(true);
    try {
      const { book, chapters } = await fetchBookChapters(input);
      let existing = new Set();
      try {
        existing = await findExistingDois([book.doi, ...chapters.map((c) => c.doi)]);
      } catch {
        // Dedup is best-effort; a failed lookup shouldn't block the import.
      }
      const rows = chapters.map((c) => {
        const exists = !!c.doi && existing.has(c.doi.toLowerCase());
        return {
          include: !exists,
          exists,
          title: c.title,
          authors: c.authors.join('; '),
          pages: c.pages,
          doi: c.doi,
          url: c.url,
          abstract: c.abstract,
        };
      });
      // The book itself (an edited volume) can be added as its own entry.
      // Only offered when we actually resolved a book record.
      const bookAvailable = !!(book.title || book.doi);
      const bookExists = !!book.doi && existing.has(book.doi.toLowerCase());
      setIncludeBook(bookAvailable && !bookExists);
      setShared({
        booktitle: book.title,
        editors: book.editors.join('; '),
        publisher: book.publisher,
        place: '',
        year: book.year ?? '',
        language: book.language || chapters[0]?.language || '',
      });
      setData({ book: { ...book, available: bookAvailable, exists: bookExists }, rows });
    } catch (err) {
      setError(t(ERROR_KEYS[err.message] ?? 'bibliography.import.errors.fetchFailed'));
    } finally {
      setLoading(false);
    }
  }

  function updateRow(index, patch) {
    setData((prev) => ({
      ...prev,
      rows: prev.rows.map((r, i) => (i === index ? { ...r, ...patch } : r)),
    }));
  }

  function setAllIncluded(value) {
    setData((prev) => ({ ...prev, rows: prev.rows.map((r) => ({ ...r, include: value })) }));
  }

  async function handleSubmit() {
    const chosen = data.rows.filter((r) => r.include && r.title.trim());
    const withBook = includeBook && data.book.available;
    if (chosen.length === 0 && !withBook) {
      setError(t('bibliography.import.nothingSelected'));
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const langCode = shared.language;
      const language_display = langCode ? LANGUAGE_NAMES[langCode] ?? '' : '';
      const sharedMeta = {
        year: shared.year,
        language: langCode || '',
        language_display,
        publisher: shared.publisher,
        place: shared.place,
      };
      const forms = chosen.map((r) => ({
        ...EMPTY_ENTRY,
        ...sharedMeta,
        type: 'chapter',
        title: r.title,
        authors: splitPeople(r.authors),
        booktitle: shared.booktitle,
        editors: splitPeople(shared.editors),
        pages: r.pages,
        doi: r.doi,
        url: r.url,
        abstract: r.abstract,
      }));
      if (withBook) {
        // An edited volume: editors, not authors.
        forms.unshift({
          ...EMPTY_ENTRY,
          ...sharedMeta,
          type: 'book',
          title: shared.booktitle,
          editors: splitPeople(shared.editors),
          doi: data.book.doi,
          url: data.book.url,
          // Only the volume is announced, never every chapter.
          announce_in_whats_new: announceBook,
        });
      }
      const created = await createEntries(forms, { authorId, asAdmin });
      setDone({ created: created.length, skipped: data.rows.length - chosen.length });
      setData(null);
      setShared(null);
      setInput('');
    } catch (err) {
      setError(err.message ?? String(err));
    } finally {
      setBusy(false);
    }
  }

  const selectedCount =
    (data ? data.rows.filter((r) => r.include).length : 0) +
    (data?.book.available && includeBook ? 1 : 0);

  return (
    <section className="card mb-10 p-6">
      <div className="section-label">{t('bibliography.import.label')}</div>
      <h2 className="font-serif text-xl font-semibold text-cream">
        {t('bibliography.import.heading')}
      </h2>
      <p className="mt-1 text-sm text-ink-secondary">{t('bibliography.import.subheading')}</p>

      <form onSubmit={handleFetch} className="mt-4 flex flex-col gap-2 sm:flex-row">
        <input
          type="text"
          inputMode="url"
          value={input}
          onChange={(e) => {
            setInput(e.target.value);
            setError(null);
          }}
          placeholder={t('bibliography.import.inputPlaceholder')}
          className="form-input flex-1"
        />
        <button type="submit" className="btn-primary whitespace-nowrap" disabled={loading || !input.trim()}>
          {loading ? t('bibliography.import.loading') : t('bibliography.import.fetch')}
        </button>
      </form>

      {done && (
        <p className="mt-4 rounded-md border border-gold/40 bg-navy-mid/40 px-3 py-2 text-sm text-gold-light">
          {asAdmin
            ? t('bibliography.import.publishedBody', { count: done.created })
            : t('bibliography.import.submittedBody', { count: done.created })}
          {done.skipped > 0 && ` ${t('bibliography.import.skippedNote', { count: done.skipped })}`}
        </p>
      )}

      {error && (
        <p className="mt-4 rounded-md border border-red-500/40 bg-red-950/40 px-3 py-2 text-sm text-red-200">
          {error}
        </p>
      )}

      {data && shared && (
        <div className="mt-6 flex flex-col gap-6">
          {/* Book-level fields, applied to every selected chapter. */}
          <div>
            <div className="section-label">{t('bibliography.import.sharedHeading')}</div>
            <div className="grid gap-4 md:grid-cols-2">
              <Labeled label={t('bibliography.detail.booktitle')}>
                <input
                  className="form-input"
                  value={shared.booktitle}
                  onChange={(e) => setShared({ ...shared, booktitle: e.target.value })}
                />
              </Labeled>
              <Labeled label={t('bibliography.detail.editors')} hint={t('bibliography.import.peopleHint')}>
                <input
                  className="form-input"
                  value={shared.editors}
                  onChange={(e) => setShared({ ...shared, editors: e.target.value })}
                />
              </Labeled>
              <Labeled label={t('bibliography.detail.publisher')}>
                <input
                  className="form-input"
                  value={shared.publisher}
                  onChange={(e) => setShared({ ...shared, publisher: e.target.value })}
                />
              </Labeled>
              <Labeled label={t('bibliography.detail.place')}>
                <input
                  className="form-input"
                  value={shared.place}
                  onChange={(e) => setShared({ ...shared, place: e.target.value })}
                  placeholder={t('bibliography.import.placePlaceholder')}
                />
              </Labeled>
              <Labeled label={t('bibliography.form.year')}>
                <input
                  className="form-input"
                  type="number"
                  inputMode="numeric"
                  value={shared.year}
                  onChange={(e) => setShared({ ...shared, year: e.target.value })}
                />
              </Labeled>
              <Labeled label={t('bibliography.form.language')}>
                <select
                  className="form-input"
                  value={shared.language}
                  onChange={(e) => setShared({ ...shared, language: e.target.value })}
                >
                  <option value="">{t('bibliography.form.languageNone')}</option>
                  {LANGUAGE_OPTIONS.map((l) => (
                    <option key={l.code} value={l.code}>
                      {l.name}
                    </option>
                  ))}
                </select>
              </Labeled>
            </div>
          </div>

          {/* Chapter checklist. */}
          <div>
            <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
              <div className="section-label !mb-0">
                {t('bibliography.import.chaptersFound', { count: data.rows.length })} ·{' '}
                {t('bibliography.import.selectedCount', { count: selectedCount })}
              </div>
              <div className="flex gap-3 text-xs">
                <button type="button" className="text-gold hover:underline" onClick={() => setAllIncluded(true)}>
                  {t('bibliography.import.selectAll')}
                </button>
                <button
                  type="button"
                  className="text-ink-secondary hover:underline"
                  onClick={() => setAllIncluded(false)}
                >
                  {t('bibliography.import.deselectAll')}
                </button>
              </div>
            </div>

            {data.book.available && (
              <label
                className={`mb-3 flex items-start gap-3 rounded-md border border-gold/30 bg-navy-mid/30 p-3 ${
                  includeBook ? '' : 'opacity-50'
                }`}
              >
                <input
                  type="checkbox"
                  checked={includeBook}
                  onChange={(e) => setIncludeBook(e.target.checked)}
                  className="mt-1 h-4 w-4 shrink-0 accent-gold"
                />
                <span className="flex flex-col gap-0.5">
                  <span className="text-[0.65rem] font-semibold uppercase tracking-[0.15em] text-gold">
                    {t('bibliography.import.wholeBook')}
                  </span>
                  <span className="text-sm font-medium text-cream">
                    {shared.booktitle || t('bibliography.types.book')}
                  </span>
                  <span className="text-xs text-ink-secondary">
                    {t('bibliography.import.bookSubtitle')}
                  </span>
                  {data.book.exists && (
                    <span className="text-xs text-gold-light/80">
                      {t('bibliography.import.alreadyInLibrary')}
                    </span>
                  )}
                </span>
              </label>
            )}

            <ul className="flex flex-col divide-y divide-white/5 rounded-md border border-white/10">
              {data.rows.map((row, i) => (
                <li
                  key={row.doi || i}
                  className={`flex flex-col gap-2 p-3 sm:flex-row sm:items-start ${
                    row.include ? '' : 'opacity-50'
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={row.include}
                    onChange={(e) => updateRow(i, { include: e.target.checked })}
                    className="mt-2.5 h-4 w-4 shrink-0 accent-gold"
                  />
                  <div className="flex flex-1 flex-col gap-2">
                    <input
                      className="form-input"
                      value={row.title}
                      onChange={(e) => updateRow(i, { title: e.target.value })}
                      placeholder={t('bibliography.form.title')}
                    />
                    <div className="flex flex-col gap-2 sm:flex-row">
                      <input
                        className="form-input flex-1"
                        value={row.authors}
                        onChange={(e) => updateRow(i, { authors: e.target.value })}
                        placeholder={t('bibliography.form.authors')}
                      />
                      <input
                        className="form-input sm:w-28"
                        value={row.pages}
                        onChange={(e) => updateRow(i, { pages: e.target.value })}
                        placeholder={t('bibliography.detail.pages')}
                      />
                    </div>
                    {row.exists && (
                      <span className="text-xs text-gold-light/80">
                        {t('bibliography.import.alreadyInLibrary')}
                      </span>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          </div>

          {data.book.available && includeBook && (
            <div className="mb-4">
              <AnnounceCheckbox checked={announceBook} onChange={setAnnounceBook} />
              <p className="mt-1 text-xs text-ink-muted">{t('bibliography.import.announceHint')}</p>
            </div>
          )}

          <div className="flex flex-wrap items-center justify-end gap-3">
            <button
              type="button"
              className="btn-ghost"
              onClick={() => {
                setData(null);
                setShared(null);
              }}
              disabled={busy}
            >
              {t('common.cancel')}
            </button>
            <button type="button" className="btn-primary" onClick={handleSubmit} disabled={busy}>
              {busy
                ? t('common.loading')
                : asAdmin
                  ? t('bibliography.import.publish', { count: selectedCount })
                  : t('bibliography.import.submit', { count: selectedCount })}
            </button>
          </div>
        </div>
      )}
    </section>
  );
}

function Labeled({ label, hint, children }) {
  return (
    <label className="block">
      <span className="mb-1 block text-xs font-medium uppercase tracking-[0.12em] text-ink-secondary">
        {label}
      </span>
      {children}
      {hint && <span className="mt-1 block text-xs text-ink-muted">{hint}</span>}
    </label>
  );
}
